import React, { useState, useEffect } from 'react';
import { CircularProgress } from './CircularProgress';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
// import { EmailReminderBanner } from './EmailReminderBanner'; // Temporarily commented out for debugging
import { clockStateManager, type ClockState } from '../utils/clockState';
import { useAuth } from '../hooks/useAuth';
import { 
  Clock, 
  DollarSign, 
  Shield, 
  TrendingUp,
  Calendar,
  MapPin,
  Coffee,
  Camera,
  CheckCircle,
  Eye,
  EyeOff,
  Play,
  LogOut,
  User
} from 'lucide-react';

interface ClockItDashboardProps {
  user?: any; // User object from Supabase auth
  onNavigate?: (screen: ScreenType) => void;
  onClockAction?: () => Promise<void>;
  onViewDailyReport?: (date?: string) => void;
  onNavigateToProfile?: () => void;
}

type ScreenType = 'login' | 'dashboard' | 'clock' | 'settings' | 'bulk-approval' | 'debug-admin' | 'superuser-setup' | 'admin';

export function ClockItDashboard({ user, onViewDailyReport, onClockAction, onNavigateToProfile, onNavigate }: ClockItDashboardProps) {
  const today = new Date().toISOString().split('T')[0];
  const targetHours = 8;
  
  // Get auth status for admin access
  const { hasAdminAccess, hasSuperAdminAccess } = useAuth();
  
  // Get profile completion status
  const getProfileCompletion = () => {
    const savedProfile = localStorage.getItem('clockit-profile');
    let profile = { hourlyWage: 0, payFrequency: '', workplace: '', jobTitle: '' };
    
    if (savedProfile) {
      try {
        profile = { ...profile, ...JSON.parse(savedProfile) };
      } catch (e) {
        console.warn('Could not parse saved profile');
      }
    }
    
    const essentialFields = [
      profile.hourlyWage > 0,
      profile.payFrequency !== '',
      profile.workplace !== '',
      profile.jobTitle !== ''
    ];
    
    const completed = essentialFields.filter(Boolean).length;
    const percentage = Math.round((completed / essentialFields.length) * 100);
    
    return {
      percentage,
      isComplete: percentage === 100,
      profile,
      missingEssentials: essentialFields.filter(field => !field).length
    };
  };
  
  const profileStatus = getProfileCompletion();
  const hourlyRate = profileStatus.profile.hourlyWage || 18.50;
  
  // Clock state management
  const [clockState, setClockState] = useState<ClockState>(clockStateManager.getState());
  
  // Privacy-first: earnings blurred by default
  const [earningsBlurred, setEarningsBlurred] = useState(true);

  useEffect(() => {
    const unsubscribe = clockStateManager.subscribe(setClockState);
    return unsubscribe;
  }, []);

  const hoursWorked = clockStateManager.getWorkedHours().toFixed(2);
  const earnedToday = parseFloat(hoursWorked) * hourlyRate;
  const progressPercentage = (parseFloat(hoursWorked) / targetHours) * 100;
  
  const { isClockedIn, hasPhotoProof, isOnBreak, isOnLunch } = clockState;

  // Mock data for week progress
  const getWeekDates = () => {
    const dates = [];
    const currentDate = new Date();
    const monday = new Date(currentDate);
    monday.setDate(currentDate.getDate() - currentDate.getDay() + 1);
    
    for (let i = 0; i < 5; i++) {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      dates.push(date.toISOString().split('T')[0]);
    }
    return dates;
  };

  const weekDates = getWeekDates();
  const weekData = [
    { day: 'Mon', hours: 8, target: 8, date: weekDates[0] },
    { day: 'Tue', hours: 7.5, target: 8, date: weekDates[1] },
    { day: 'Wed', hours: 6.5, target: 8, date: weekDates[2] },
    { day: 'Thu', hours: 0, target: 8, date: weekDates[3] },
    { day: 'Fri', hours: 0, target: 8, date: weekDates[4] },
  ];

  return (
    <div className="pb-20 bg-background min-h-screen">


      {/* Profile Completion Reminder - Show if profile is incomplete */}
      {!profileStatus.isComplete && (
        <div className="px-4 pt-4">
          <Alert className="border-warning bg-warning/5">
            <User className="h-4 w-4 text-warning" />
            <AlertDescription className="text-warning">
              <div className="flex items-center justify-between">
                <div>
                  <strong>Unlock Full Protection!</strong> Complete your profile to enable wage tracking, overtime alerts, and stronger dispute assistance. ({profileStatus.percentage}% complete)
                </div>
                <Button
                  onClick={() => onNavigateToProfile?.()}
                  size="sm"
                  className="ml-3 bg-warning hover:bg-warning/90 text-white h-8 px-3 text-xs font-medium"
                >
                  <User className="w-3 h-3 mr-1" />
                  Complete Profile
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        </div>
      )}

      {/* Photo Proof Status Alert */}
      {isClockedIn && !hasPhotoProof && (
        <div className="px-4 pt-4">
          <Alert className="border-primary bg-primary/10">
            <Camera className="h-4 w-4 text-primary" />
            <AlertDescription className="text-primary">
              <div className="flex items-center justify-between">
                <div>
                  <strong>Reminder!</strong> You're clocked in without a photo. Add one for the best hour documentation.
                </div>
                <Button
                  onClick={() => {
                    console.log('📸 Adding photo to current session from reminder');
                    onClockAction?.();
                  }}
                  size="sm"
                  className="ml-3 bg-primary hover:bg-primary/90 text-white h-8 px-3 text-xs font-medium"
                >
                  <Camera className="w-3 h-3 mr-1" />
                  Add Photo
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        </div>
      )}

      {isClockedIn && hasPhotoProof && (
        <div className="px-4 pt-4">
          <Alert className="border-success bg-success/10">
            <CheckCircle className="h-4 w-4 text-success" />
            <AlertDescription className="text-success">
              <strong>Excellent!</strong> You have photo documentation. Your hours are well-recorded.
            </AlertDescription>
          </Alert>
        </div>
      )}

      {/* Email Verification Reminder Banner - Temporarily commented out for debugging */}
      <div className="px-4">
        {/* <EmailReminderBanner /> */}
      </div>

      {/* Main Progress Section */}
      <div className="px-4 py-6">
        <div className="clock-it-card card-content text-center">
          <div className="flex justify-center mb-4">
            <CircularProgress 
              percentage={progressPercentage}
              size={140}
              strokeWidth={12}
              color="#0891b2"
            >
              <div className="text-center">
                <div className="text-metric text-foreground">{hoursWorked}</div>
                <div className="text-caption">hours today</div>
              </div>
            </CircularProgress>
          </div>
          
          <div className="flex justify-center space-x-6 mb-6">
            <div className="text-center">
              <div className="w-4 h-4 rounded-full bg-success mx-auto mb-1 shadow-sm"></div>
              <div className="text-sm font-medium">{hoursWorked}</div>
              <div className="text-caption">Hours worked</div>
            </div>
            <div className="text-center">
              <div className="w-4 h-4 rounded-full bg-primary mx-auto mb-1 shadow-sm"></div>
              <div className="text-sm font-medium">
                {profileStatus.profile.hourlyWage === 0 ? 'Set wage' : 
                 earningsBlurred ? '••••' : `${earnedToday.toFixed(0)}`}
              </div>
              <div className="text-caption">Earned today</div>
            </div>
            <div className="text-center">
              <div className={`w-4 h-4 rounded-full mx-auto mb-1 shadow-sm ${
                hasPhotoProof ? 'bg-success' : 'bg-muted'
              }`}></div>
              <div className="text-sm font-medium">{hasPhotoProof ? 'Complete' : 'Basic'}</div>
              <div className="text-caption">Documentation</div>
            </div>
          </div>

          <div className="space-y-3">
            <Button 
              onClick={() => {
                // Add some feedback before navigation
                console.log(isClockedIn ? '📸 Opening camera for clock out' : '📸 Opening camera for clock in');
                onClockAction?.();
              }}
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground rounded-full h-12 text-base font-semibold shadow-lg shadow-primary/25 transition-all duration-200 active:scale-95"
            >
              <Camera className="w-5 h-5 mr-2" />
              {isClockedIn ? 'Take Photo & Clock Out' : 'Take Photo & Clock In'}
            </Button>

            {/* Quick Action Buttons - Show 3 buttons when clocked in */}
            {isClockedIn && (
              <div className="space-y-2">
                <div className="flex gap-2">
                  {/* Clock Out Button */}
                  <Button
                    onClick={() => clockStateManager.clockOut()}
                    variant="outline"
                    size="sm"
                    className="flex-1 rounded-full text-xs h-9 hover:bg-destructive hover:text-destructive-foreground transition-colors"
                  >
                    <LogOut className="w-3 h-3 mr-1" />
                    Clock Out
                  </Button>
                  
                  {/* Lunch Button */}
                  <Button
                    onClick={() => {
                      if (isOnLunch) {
                        clockStateManager.endLunch();
                      } else {
                        clockStateManager.startLunch();
                      }
                    }}
                    variant={isOnLunch ? "default" : "outline"}
                    size="sm"
                    className={`flex-1 rounded-full text-xs h-9 transition-all duration-200 ${
                      isOnLunch 
                        ? 'bg-orange-500 hover:bg-orange-600 text-white animate-pulse' 
                        : 'hover:bg-orange-50 hover:border-orange-300 hover:text-orange-700'
                    } ${isOnBreak ? 'opacity-50' : ''}`}
                    disabled={isOnBreak}
                  >
                    <span className="mr-1">🍽️</span>
                    {isOnLunch ? 'End Lunch' : 'Lunch'}
                  </Button>
                  
                  {/* Break Button */}
                  <Button
                    onClick={() => {
                      if (isOnBreak) {
                        clockStateManager.endBreak();
                      } else {
                        clockStateManager.startBreak();
                      }
                    }}
                    variant={isOnBreak ? "default" : "outline"}
                    size="sm"
                    className={`flex-1 rounded-full text-xs h-9 transition-all duration-200 ${
                      isOnBreak 
                        ? 'bg-amber-500 hover:bg-amber-600 text-white animate-pulse' 
                        : 'hover:bg-amber-50 hover:border-amber-300 hover:text-amber-700'
                    } ${isOnLunch ? 'opacity-50' : ''}`}
                    disabled={isOnLunch}
                  >
                    <Coffee className="w-3 h-3 mr-1" />
                    {isOnBreak ? 'End Break' : 'Break'}
                  </Button>
                </div>

                {/* Status indicator for current break/lunch */}
                {(isOnBreak || isOnLunch) && (
                  <div className="text-center">
                    <Badge 
                      variant={isOnLunch ? "default" : "secondary"} 
                      className={`text-xs transition-all ${
                        isOnLunch 
                          ? 'bg-orange-500 text-white border-orange-500' 
                          : 'bg-amber-500 text-white border-amber-500'
                      }`}
                    >
                      {isOnBreak && `☕ Break: ${Math.floor(clockStateManager.getBreakDuration())} min`}
                      {isOnLunch && `🍽️ Lunch: ${Math.floor(clockStateManager.getLunchDuration())} min`}
                    </Badge>
                  </div>
                )}
              </div>
            )}

            {/* Alternative quick clock in when not clocked in */}
            {!isClockedIn && (
              <Button
                onClick={() => clockStateManager.clockIn()}
                variant="outline"
                className="w-full rounded-full h-10 text-sm"
              >
                <Play className="w-4 h-4 mr-2" />
                Clock In (Skip Photo)
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions Grid */}
      <div className="px-4 mb-6">
        <div className="clock-it-grid clock-it-grid-2">
          {/* Photo Proof Status - Most Important */}
          <div className={`clock-it-card card-content text-white ${
            hasPhotoProof ? 'gradient-green' : 'gradient-cyan'
          }`}>
            <div className="flex items-center justify-between mb-2">
              <Camera className="w-6 h-6" />
              <Badge className="bg-white/20 text-white border-0">
                {hasPhotoProof ? 'Complete' : 'Basic'}
              </Badge>
            </div>
            <div className="text-2xl font-bold mb-1">
              {hasPhotoProof ? 'Documented' : 'Add Photo'}
            </div>
            <div className="text-sm opacity-90">
              {hasPhotoProof ? 'Photo documentation ready' : 'Strengthen your records'}
            </div>
          </div>

          {/* Earnings Card */}
          <div className="clock-it-card card-content gradient-indigo text-white">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="w-6 h-6" />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEarningsBlurred(!earningsBlurred)}
                className="bg-white/20 text-white hover:bg-white/30 h-auto p-1.5"
              >
                {earningsBlurred ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </Button>
            </div>
            <div className="text-2xl font-bold mb-1">
              {profileStatus.profile.hourlyWage === 0 ? '--' : 
               earningsBlurred ? '••••••' : `${earnedToday.toFixed(2)}`}
            </div>
            <div className="text-sm opacity-90">
              {profileStatus.profile.hourlyWage === 0 ? 'Complete profile to track earnings' :
               earningsBlurred ? 'Tap to show earnings' : 'Fair pay guaranteed ✓'}
            </div>
          </div>
        </div>
      </div>

      {/* Week Progress */}
      <div className="px-4 mb-6">
        <div className="clock-it-card card-content">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-semibold">This Week</h3>
              <p className="text-xs text-muted-foreground">Tap any day for details</p>
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => onViewDailyReport?.(today)}
              className="text-primary hover:text-primary/80"
            >
              See all
            </Button>
          </div>
          
          <div className="flex justify-between items-end space-x-2 mb-4">
            {weekData.map((day) => (
              <button
                key={day.day}
                onClick={() => onViewDailyReport?.(day.date)}
                className="flex-1 text-center hover:bg-primary/5 hover:border-primary/20 border border-transparent rounded-lg p-2 transition-all duration-200 active:scale-95 group cursor-pointer"
                title={`View detailed report for ${day.day} (${day.hours} hours worked)`}
              >
                <div className="relative h-16 mb-2">
                  <div className="absolute bottom-0 w-full bg-muted rounded-t-lg" style={{ height: '100%' }} />
                  <div 
                    className="absolute bottom-0 w-full bg-primary rounded-t-lg transition-all duration-500 group-hover:bg-primary/80" 
                    style={{ height: `${(day.hours / day.target) * 100}%` }}
                  />
                </div>
                <div className="text-caption group-hover:text-primary transition-colors">{day.day}</div>
                <div className="text-sm font-medium group-hover:text-primary transition-colors">{day.hours}h</div>
              </button>
            ))}
          </div>
          
          <div className="text-center">
            <div className="text-sm text-muted-foreground">
              {weekData.reduce((sum, day) => sum + day.hours, 0)}h of {weekData.reduce((sum, day) => sum + day.target, 0)}h goal
            </div>
          </div>
        </div>
      </div>

      {/* Activity Cards */}
      <div className="px-4 mb-6">
        {/* Profile Completion Education Section - Show if incomplete */}
        {!profileStatus.isComplete && (
          <div className="px-4 mb-6">
            <div className="clock-it-card card-content border-2 border-warning bg-warning/5">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-warning rounded-full flex items-center justify-center flex-shrink-0">
                  <User className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-warning mb-2">Complete Your Profile for Maximum Protection</h3>
                  <p className="text-sm text-foreground mb-3">
                    Adding your wage and work details unlocks powerful features that help protect your earnings:
                  </p>
                  <div className="space-y-1 text-xs mb-3">
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-3 h-3" />
                      <span>Automatic overtime detection & alerts</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Shield className="w-3 h-3" />
                      <span>Accurate wage tracking & underpayment alerts</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-3 h-3" />
                      <span>Smart shift reminders & scheduling</span>
                    </div>
                  </div>
                  <Button 
                    onClick={() => onNavigateToProfile?.()}
                    size="sm"
                    className="bg-warning hover:bg-warning/90 text-white"
                  >
                    <User className="w-4 h-4 mr-2" />
                    Complete Profile ({profileStatus.percentage}% done)
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Photo Proof Education Section */}
        {!hasPhotoProof && (
          <div className="px-4 mb-6">
            <div className="clock-it-card card-content border-2 border-primary bg-primary/5">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                  <Camera className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-primary mb-2">Strengthen Your Hour Records</h3>
                  <p className="text-sm text-foreground mb-3">
                    Photos help document that you're actually at work when you clock in. 
                    This gives you the <strong>strongest proof</strong> of your hours and makes your records more complete.
                  </p>
                  <Button 
                    onClick={onClockAction}
                    size="sm"
                    className="bg-primary hover:bg-primary/90 text-white"
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    Add Photo Documentation
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="clock-it-grid clock-it-grid-3">
          <div className={`clock-it-card card-content text-white ${
            hasPhotoProof ? 'gradient-green' : 'gradient-cyan'
          }`}>
            <Shield className="w-6 h-6 mb-2" />
            <div className="text-lg font-bold">{hasPhotoProof ? 'Complete' : 'Basic'}</div>
            <div className="text-xs opacity-90">Records</div>
          </div>
          
          <div className="clock-it-card card-content gradient-blue text-white">
            <TrendingUp className="w-6 h-6 mb-2" />
            <div className="text-lg font-bold">15%</div>
            <div className="text-xs opacity-90">Week Growth</div>
          </div>
          
          <div className="clock-it-card card-content gradient-purple text-white">
            <Calendar className="w-6 h-6 mb-2" />
            <div className="text-lg font-bold">3</div>
            <div className="text-xs opacity-90">Days Left</div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="px-4 mb-6">
        <div className="clock-it-card card-content">
          <h3 className="font-semibold mb-4">Recent Activity</h3>
          
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-success/10 rounded-full flex items-center justify-center">
                <Clock className="w-5 h-5 text-success" />
              </div>
              <div className="flex-1">
                <div className="font-medium">Clocked out</div>
                <div className="text-caption">Today at 5:30 PM • 8.5 hours</div>
              </div>
              <Badge className="status-verified text-xs">Verified</Badge>
            </div>
            
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                <MapPin className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1">
                <div className="font-medium">Location verified</div>
                <div className="text-caption">Today at 9:00 AM • Main Office</div>
              </div>
              <Badge className="status-verified text-xs">GPS</Badge>
            </div>
            
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-warning/10 rounded-full flex items-center justify-center">
                <Coffee className="w-5 h-5 text-warning" />
              </div>
              <div className="flex-1">
                <div className="font-medium">Break started</div>
                <div className="text-caption">Today at 12:30 PM • 30 minutes</div>
              </div>
              <Badge variant="outline" className="text-xs">Manual</Badge>
            </div>
          </div>
        </div>
      </div>

      {/* TEMPORARY BOOTSTRAP BUTTON - Only for apiretti@gmail.com to become first superuser */}
      {onNavigate && user?.email === 'apiretti@gmail.com' && !hasSuperAdminAccess && (
        <div className="mt-4 px-6">
          <Button 
            onClick={() => onNavigate('superuser-setup')}
            variant="outline"
            className="w-full bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100"
          >
            ⚡ BOOTSTRAP: Become First Superuser
          </Button>
          <p className="text-xs text-amber-600 text-center mt-2">
            This button will disappear once you become a superuser
          </p>
        </div>
      )}

      {/* ADMIN ACCESS BUTTON - Shows for superusers */}
      {onNavigate && (hasAdminAccess || hasSuperAdminAccess) && (
        <div className="mt-4 px-6">
          <Button 
            onClick={() => onNavigate('admin')}
            variant="outline"
            className="w-full bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
          >
            <Shield className="w-4 h-4 mr-2" />
            {hasSuperAdminAccess ? 'Super Admin Dashboard' : 'Admin Dashboard'}
          </Button>
          <p className="text-xs text-blue-600 text-center mt-2">
            Access administrative functions and system controls
          </p>
        </div>
      )}

    </div>
  );
}